import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns/esm';

import { useAppSelector } from '../../../store/store';
import { IVitalSign, selectVitalSigns } from '../../../store/slices/vitalSigns';
import { toast } from 'react-toastify';
import { client } from '../../../networking/client';
import { AxiosResponse } from 'axios';
import { VitalSignResponse } from './VitalSignsTab';
import CustomTextField from '../../../components/UI/Inputs/CustomTextField';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import classes from './VitalSignsTab.module.css';
import WithLoader from '../../../components/WithLoader/WithLoader';
import VitalSignResults from './VitalSignResult';
import moment from 'moment';
import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import { useTranslation } from '../../../hooks/useTranslations';
interface validationType {
  [key: string]: boolean;
}
interface IsConfirmedValidationTypes {
  validation: validationType;
}
const EditVitalSignsCard = ({ serviceName, item, selectedSalesOrder, addManualy }: any) => {
  const { translations } = useTranslation();
  const vitalSigns = useAppSelector(selectVitalSigns);
  const [isLoading, setIsLading] = useState(true);

  const stateObject: any = {
    date: format(new Date(), 'dd.MM.yyyy'),
    time: format(new Date(), 'HH:mm'),
    notes: '',
  };
  item.map((el: any) => el.vital_sign_name).forEach((el: any) => (stateObject[el] = ''));

  const [dynamicInput, setDynamicInput] = React.useState<any>(stateObject);
  const [isConfirmedValidation, setIsConfirmedValidation] = useState<IsConfirmedValidationTypes>({
    validation: {},
  });

  const validateVitalSigns = (): boolean => {
    let validation: validationType = {};
    let isValid = true;

    item.forEach((el: IVitalSign) => {
      const value = dynamicInput[el.vital_sign_name];
      let minimumValue;
      let maximumValue;
      if (el.vital_sign_name === 'Blood Glucose') {
        const obj = el.vital_sign_references.find(
          (el) =>
            el.mealtime_id === (dynamicInput.Mealtime ? parseInt(dynamicInput.Mealtime) : null) &&
            el.biomarker_uom_id === (dynamicInput.Unit ? parseInt(dynamicInput.Unit) : null)
        );
        minimumValue = obj?.minimum_value;
        maximumValue = obj?.maximum_value;
      } else {
        minimumValue = el?.vital_sign_references?.[0]?.minimum_value;
        maximumValue = el?.vital_sign_references?.[0]?.maximum_value;
      }

      // Blood Glucose VitalSign, all inputs will be mandatory
      const isMandatory =
        el.vital_sign_group_name === 'Blood Glucose'
          ? true
          : el.services.find((id) => id.service_id === selectedSalesOrder.service.service_id)
            ?.is_mandatory;

      if (!value && !isMandatory) {
        validation[el.vital_sign_name] = false;
      } else if (!value && isMandatory) {
        validation[el.vital_sign_name] = true;
        isValid = false;
      } else if (minimumValue !== null && maximumValue !== null) {
        if (Number(value) < Number(minimumValue) || Number(value) > Number(maximumValue)) {
          validation[el.vital_sign_name] = true;
          isValid = false;
        } else {
          validation[el.vital_sign_name] = false;
        }
      } else {
        validation[el.vital_sign_name] = false;
      }
    });

    setIsConfirmedValidation((prevState: IsConfirmedValidationTypes) => ({
      ...prevState,
      validation: validation,
    }));

    return isValid;
  };

  const handleInput = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setDynamicInput((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));

    let validation: validationType = {};
    const currentVitalSign = item.find((el: IVitalSign) => el.vital_sign_name === name);
    if (currentVitalSign) {
      const { minimum_value, maximum_value } = currentVitalSign;

      if (!value) {
        validation[name] = false;
      } else if (minimum_value !== null && maximum_value !== null) {
        const numericValue = Number(value);
        if (numericValue < minimum_value || numericValue > maximum_value) {
          validation[name] = true;
        } else {
          validation[name] = false;
        }
      } else {
        validation[name] = false;
      }
    }

    setIsConfirmedValidation((prevState: IsConfirmedValidationTypes) => ({
      ...prevState,
      validation: { ...prevState.validation, ...validation },
    }));
  };

  useEffect(() => {
    if (vitalSigns.length !== 0) {
      if (item[0].vital_sign_group_name === 'Temperature') {
        const fetchTemperature = async () => {
          setIsLading(true);
          const response = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${vitalSignTemperature.vital_sign_id}`
          );
          if (response?.data?.vitalSignResult?.vital_sign_value_result) {
            setDynamicInput({
              ...dynamicInput,
              Temperature: response.data.vitalSignResult.vital_sign_value_result || '',
            });
          }
          setIsLading(false);
        };

        fetchTemperature();
      }
      if (item[0].vital_sign_group_name === 'Blood Pressure') {
        const fetchBloodPressure = async () => {
          setIsLading(true);
          try {
            const systolicRes: AxiosResponse<VitalSignResponse> = await client.get(
              `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${SYSTOLIC_ID}`
            );
            const pulseRes: AxiosResponse<VitalSignResponse> = await client.get(
              `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PULSE_ID}`
            );
            const diaRes: AxiosResponse<VitalSignResponse> = await client.get(
              `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${DIA_ID}`
            );
            setDynamicInput({
              ...dynamicInput,
              Systolic: systolicRes?.data?.vitalSignResult?.vital_sign_value_result || '',
              Pulse: pulseRes.data.vitalSignResult?.vital_sign_value_result || '',
              Diastolic: diaRes.data.vitalSignResult?.vital_sign_value_result || '',
            });
          } catch (error) {
            toast.error(translations('vitalsigns.body.fetch.message'), {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
          setIsLading(false);
        };

        fetchBloodPressure();
      }
      if (item[0].vital_sign_group_name === 'Body Composition') {
        const fetchBodyComposition = async () => {
          setIsLading(true);

          const bmiRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BMI_ID}`
          );
          const bodyFatRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_FAT_ID}`
          );
          const tkaRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${TKA_ID}`
          );
          const leanMuscleMassRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${LEAN_MUSCLE_MASS_ID}`
          );
          const boneMassRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BONE_MASS_ID}`
          );
          const muscleMassRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${MUSCLE_MASS_ID}`
          );
          const bodyWaterRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_WATER_ID}`
          );
          const vfrRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${VFR_ID}`
          );
          const bodyWeightRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_WEIGHT_ID}`
          );

          setDynamicInput({
            ...dynamicInput,
            'BMI Classification': bmiRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Body Fat': bodyFatRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            TKA: tkaRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Lean Muscle Mass':
              leanMuscleMassRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Bone Mass': boneMassRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Muscle Mass': muscleMassRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Body Water': bodyWaterRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            VFR: vfrRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Body Weight': bodyWeightRes?.data?.vitalSignResult?.result_weight || '',
          });
          setIsLading(false);
        };
        fetchBodyComposition();
      }
      if (item[0].vital_sign_group_name === 'Oxygen and Pulse Rate') {
        const fetchOxygenAndPulseRate = async () => {
          setIsLading(true);
          const oxygenRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${OXYGEN_ID}`
          );
          const pulseRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PULSE_RATE_ID}`
          );
          const piRes: AxiosResponse<VitalSignResponse> = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PI}`
          );
          setDynamicInput({
            ...dynamicInput,
            SpO2: oxygenRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            'Pulse Rate': pulseRes?.data?.vitalSignResult?.vital_sign_value_result || '',
            PI: piRes?.data?.vitalSignResult?.vital_sign_value_result || '',
          });
          setIsLading(false);
        };
        fetchOxygenAndPulseRate();
      }
      if (item[0].vital_sign_group_name === 'Blood Glucose') {
        const fetchBloodGlucose = async () => {
          setIsLading(true);
          const response = await client.get(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${vitalSignBloodGlucose.vital_sign_id}`
          );
          // if (response?.data?.vitalSignResult?.vital_sign_value_result) {
          setDynamicInput({
            ...dynamicInput,
            'Blood Glucose': response.data.vitalSignResult.vital_sign_value_result || '',
            Mealtime: response.data?.vitalSignResult?.mealtime_id || '',
            Unit: response.data?.vitalSignResult?.biomarker_uom_id || 1,
          });
          // }
          setIsLading(false);
        };
        fetchBloodGlucose();
      }
    }
  }, [vitalSigns]);

  const SYSTOLIC_ID =
    vitalSigns.find((vs) => vs.vital_sign_name === 'Systolic')?.vital_sign_id ?? -1;
  const DIA_ID =
    vitalSigns.find((vs) => vs.vital_sign_name === 'DIA' || vs.vital_sign_name === 'Diastolic')
      ?.vital_sign_id ?? -1;
  const PULSE_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'Pulse')?.vital_sign_id ?? -1;
  const PULSE_RATE_ID =
    vitalSigns.find((vs) => vs.vital_sign_name === 'Pulse Rate')?.vital_sign_id ?? -1;

  const BODY_FAT_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'Body Fat')?.vital_sign_id;
  const TKA_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'TKA')?.vital_sign_id;
  const MUSCLE_MASS_ID = vitalSigns.find(
    (vs) => vs.vital_sign_name === 'Muscle Mass'
  )?.vital_sign_id;
  const LEAN_MUSCLE_MASS_ID = vitalSigns.find(
    (vs) => vs.vital_sign_name === 'Lean Muscle Mass'
  )?.vital_sign_id;
  const BONE_MASS_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'Bone Mass')?.vital_sign_id;
  const BODY_WATER_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'Body Water')?.vital_sign_id;
  const BMI_ID = vitalSigns.find(
    (vs) => vs.vital_sign_name === 'BMI Classification'
  )?.vital_sign_id;
  const VFR_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'VFR')?.vital_sign_id;
  const BODY_WEIGHT_ID = vitalSigns.find(
    (vs) => vs.vital_sign_name === 'Body Weight'
  )?.vital_sign_id;

  const OXYGEN_ID = vitalSigns.find((vs) => vs.vital_sign_name === 'SpO2')?.vital_sign_id;
  const PI = vitalSigns.find((vs) => vs.vital_sign_name === 'PI')?.vital_sign_id;

  const vitalSignTemperature: any = useMemo(() => {
    return vitalSigns.find((vs) => vs.vital_sign_name === 'Temperature');
  }, [vitalSigns]);

  const vitalSignBloodGlucose: any = useMemo(() => {
    return vitalSigns.find((vs) => vs.vital_sign_name === 'Blood Glucose');
  }, [vitalSigns]);

  const handleConfirm = async () => {
    const isValid = validateVitalSigns();
    if (!isValid) {
      await toast.error(translations('Please provide all the vital sign values'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }
    try {
      setIsLading(true);
      if (item[0].vital_sign_group_name === 'Temperature') {
        if (vitalSignTemperature) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${vitalSignTemperature.vital_sign_id}`,
            {
              vitalSignValueResult: parseFloat(dynamicInput['Temperature']).toFixed(2),
            }
          );
        }
        toast.success(translations('label.successfully_set.temperature'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
      if (item[0].vital_sign_group_name === 'Blood Glucose') {
        await client.put(
          `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${vitalSignBloodGlucose.vital_sign_id}`,
          {
            vitalSignValueResult: parseFloat(dynamicInput['Blood Glucose']),
            vitalSignMealTimeId: parseFloat(dynamicInput['Mealtime']),
            vitalSignBiomarkerUomId: parseFloat(dynamicInput['Unit']),
          }
        );
        toast.success(translations('label.successfully_set.blood_glucose'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
      if (item[0].vital_sign_group_name === 'Blood Pressure') {
        if (dynamicInput['Systolic']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${SYSTOLIC_ID}`,
            {
              vitalSignValueResult: dynamicInput['Systolic'] || null,
            }
          );
        }

        if (dynamicInput['Diastolic']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${DIA_ID}`,
            {
              vitalSignValueResult: dynamicInput['Diastolic'] || null,
            }
          );
        }

        if (dynamicInput['Pulse']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PULSE_ID}`,
            {
              vitalSignValueResult: dynamicInput['Pulse'] || null,
            }
          );
        }
        toast.success(translations('label.successfully_set.blood_presure'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
      if (item[0].vital_sign_group_name === 'Body Composition') {
        if (dynamicInput['Body Fat']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_FAT_ID}`,
            {
              vitalSignValueResult: dynamicInput['Body Fat'] || null,
            }
          );
        }

        if (dynamicInput.TKA) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${TKA_ID}`,
            {
              vitalSignValueResult: dynamicInput.TKA || null,
            }
          );
        }

        if (dynamicInput['Lean Muscle Mass']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${LEAN_MUSCLE_MASS_ID}`,
            {
              vitalSignValueResult: dynamicInput['Lean Muscle Mass'] || null,
            }
          );
        }

        if (dynamicInput['Bone Mass']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BONE_MASS_ID}`,
            {
              vitalSignValueResult: dynamicInput['Bone Mass'] || null,
            }
          );
        }

        if (dynamicInput['Body Water']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_WATER_ID}`,
            {
              vitalSignValueResult: dynamicInput['Body Water'] || null,
            }
          );
        }

        if (dynamicInput['BMI Classification']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BMI_ID}`,
            {
              vitalSignValueResult: dynamicInput['BMI Classification'] || null,
            }
          );
        }

        if (dynamicInput['Muscle Mass']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${MUSCLE_MASS_ID}`,
            {
              vitalSignValueResult: dynamicInput['Muscle Mass'] || null,
            }
          );
        }

        if (dynamicInput.VFR) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${VFR_ID}`,
            {
              vitalSignValueResult: dynamicInput.VFR || null,
            }
          );
        }

        if (dynamicInput['Body Weight']) {
          const BMI_UOM_ID = vitalSigns.find(
            (vs) => vs.vital_sign_name === 'BMI Classification'
          )?.vital_sign_uom_id;

          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${BODY_WEIGHT_ID}`,
            {
              vitalSignValueResult: dynamicInput['Body Weight'] || null,
              typeOfVital: 'BMI',
              vitalSignBiomarkerUomId: BMI_UOM_ID
            }
          );
        }

        toast.success(translations('label.successfully_set.body_composition'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
      if (item[0].vital_sign_group_name === 'Oxygen and Pulse Rate') {
        if (dynamicInput['Pulse Rate']) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PULSE_RATE_ID}`,
            {
              vitalSignValueResult: dynamicInput['Pulse Rate'] || null,
            }
          );
        }

        if (dynamicInput.SpO2) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${OXYGEN_ID}`,
            {
              vitalSignValueResult: dynamicInput.SpO2 || null,
            }
          );
        }

        if (dynamicInput.PI) {
          await client.put(
            `/healthpoint/vital_sign_result/${selectedSalesOrder.work_order?.work_order_id}/${PI}`,
            {
              vitalSignValueResult: dynamicInput.PI || null,
            }
          );
        }
        toast.success(translations('label.successfully_set.oxygen'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
    } catch (err: any) {
      setIsLading(false);
      toast.error(err.response.data.error || 'Something went wrong!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLading(false);
    }
  };

  const disableButton = Object.entries(dynamicInput)
    .filter(([key, value]) => key !== 'date' && key !== 'time')
    .every(([key, value]) => value === '');

  const glucoseMealTimeOption = [
    { id: 1, label: translations(`vitalsigns.mealTime.1`) },
    { id: 2, label: translations('vitalsigns.mealTime.2') },
    { id: 3, label: translations('vitalsigns.mealTime.3') },
  ];

  const glucoseUnitOption = [
    { id: 1, label: 'mg/dL' },
    { id: 7, label: 'mmol/L' },
  ];

  return (
    <div key={serviceName} className={classes.uniqueItems}>
      <div className={classes.titleWrapper}>
        <p>{translations(`vitalSigns.${item[0].vital_sign_group_name.replace(/ /g, '_').toLowerCase()}`)}</p>

        {item[0].vital_sign_group_name === 'Blood Glucose' && (
          <p className={classes.smText}>
            {glucoseMealTimeOption.find((el) => el.id == dynamicInput['Mealtime'])?.label}
          </p>
        )}
      </div>
      <WithLoader isLoading={isLoading}>
        {addManualy ? (
          <>
            <div className={classes.uniqueItemsMap}>
              <div className={classes.dataField}>
                <CustomTextField
                  label={translations('vitalsigns.date') ?? ''}
                  value={moment().format('DD.MM.yyyy')}
                  onChange={handleInput}
                  name={'date'}
                  disabled
                />
              </div>
              <div className={classes.wrapper}>
                <CustomTextField
                  label={translations('vitalsigns.time') ?? ''}
                  value={moment().format('HH:mm')}
                  onChange={handleInput}
                  name={'time'}
                  disabled
                />
              </div>
            </div>

            <div className={classes.uniqueItemsMap}>
              {item.map((el: IVitalSign) => {
                const isValid = isConfirmedValidation.validation[el.vital_sign_name];
                // These vital signs are from Blood Glucose group are not returned from the API, thats why we need to handle them differently
                if (el.vital_sign_name === 'Mealtime' || el.vital_sign_name === 'Unit') {
                  return (
                    <div className={classes.wrapper} key={el.vital_sign_name}>
                      <CustomDropdown
                        errorValidation={disableButton ? false : isValid}
                        placeholder="Select"
                        label={
                          el.vital_sign_name === 'Mealtime'
                            ? translations('vitalsigns.glucose.mealtime')
                            : translations('vitalsigns.glocose.unit')
                        }
                        name={el.vital_sign_name}
                        value={
                          el.vital_sign_name === 'Mealtime'
                            ? dynamicInput['Mealtime']
                            : dynamicInput['Unit']
                        }
                        onChange={handleInput}
                        options={
                          el.vital_sign_name === 'Mealtime'
                            ? glucoseMealTimeOption
                            : glucoseUnitOption
                        }
                      />
                    </div>
                  );
                }
                return (
                  <div className={classes.wrapper} key={el.vital_sign_id}>
                    <CustomTextField
                      unit={
                        item[0].vital_sign_group_name === 'Blood Glucose'
                          ? glucoseUnitOption.find((el) => el.id == dynamicInput['Unit'])?.label
                          : el.biomarker_uom
                      }
                      label={el.vital_sign_name}
                      value={dynamicInput[el.vital_sign_name]?.toString()}
                      type={'number'}
                      name={el.vital_sign_name}
                      onChange={handleInput}
                      errorValidation={disableButton ? false : isValid}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryButtonV2
                text={'Confirm'}
                buttonStyle={{
                  height: 40,
                  fontSize: 15,
                  paddingRight: 20,
                  paddingLeft: 20,
                }}
                onClick={handleConfirm}
                disabled={disableButton}
              />
            </div>
          </>
        ) : (
          item.map((el: any, idx: number) => {
            if (el.vital_sign_name === 'Mealtime' || el.vital_sign_name === 'Unit') {
              return;
            }
            return (
              <VitalSignResults
                key={idx}
                label={translations(`vitalSignProperties.${el.vital_sign_name.replace(/ /g, '_').toLowerCase()}`
                )}
                value={
                  el?.vital_sign_name === 'Mealtime'
                    ? glucoseMealTimeOption.find((el) => el.id == dynamicInput['Mealtime'])?.label
                    : el?.vital_sign_name === 'Unit'
                      ? glucoseUnitOption.find((el) => el.id == dynamicInput['Unit'])?.label
                      : dynamicInput[el?.vital_sign_name]?.toString()
                }
                unit={
                  el?.vital_sign_name === 'Blood Glucose'
                    ? glucoseUnitOption.find((el) => el.id == dynamicInput['Unit'])?.label
                    : el?.biomarker_uom
                }
              />
            );
          })
        )}
      </WithLoader>
    </div>
  );
};

export default EditVitalSignsCard;
